import { isEmpty } from '@beeline.com/bp-design-system';
import { v4 } from 'uuid';
import type { Data, Fields, Info, RowHook, TableHook } from '../../../types';
import { ErrorSources } from '../../../types';
import type { Error as RowError, Errors, Meta } from '../types';

export const addErrorsAndRunHooks = async <T extends string>(
  data: (Data<T> & Partial<Meta>)[],
  fields: Fields<T>,
  rowHook?: RowHook<T>,
  tableHook?: TableHook<T>,
  changedRowIndexes?: number[]
): Promise<(Data<T> & Meta)[]> => {
  const errors: Errors = {};
  const errorsToRemove: { [id: string]: Set<string> } = {};

  const addError = (source: ErrorSources, rowIndex: number, fieldKey: T, error: Info | null) => {
    if (error) {
      errors[rowIndex] = {
        ...errors[rowIndex],
        [fieldKey]: { ...error, source }
      };
    } else {
      if (!errorsToRemove[rowIndex]) {
        errorsToRemove[rowIndex] = new Set<string>();
      }
      errorsToRemove[rowIndex].add(fieldKey);
    }
  };

  if (tableHook) {
    data = await tableHook(data, (...props) => addError(ErrorSources.Table, ...props));
  } else {
    data.forEach((item, index) => {
      if (item.__errors && !(changedRowIndexes || []).includes(index)) {
        errors[index] = Object.fromEntries(
          Object.entries(item.__errors || {}).filter(([, error]) => error.source === ErrorSources.Table)
        );
      }
    });
  }

  if (rowHook) {
    if (changedRowIndexes) {
      for (const index of changedRowIndexes) {
        data[index] = await rowHook(
          data[index] as Data<T>,
          (fieldKey, info, overrideRowIndex) => addError(ErrorSources.Row, overrideRowIndex ?? index, fieldKey, info),
          data
        );
      }
    } else {
      data = await Promise.all(
        data.map(async (value, index) =>
          rowHook(
            value,
            (fieldKey, info, overrideRowIndex) => addError(ErrorSources.Row, overrideRowIndex ?? index, fieldKey, info),
            data
          )
        )
      );
    }
  }

  for (const field of fields) {
    if (!field.validations) continue;

    for (const validation of field.validations) {
      switch (validation.rule) {
        case 'unique': {
          const values = data.map((entry) => entry[field.key as T]);
          const taken = new Set(); // Set of items used at least once
          const duplicates = new Set(); // Set of items used multiple times

          for (const value of values) {
            if (validation.allowEmpty && !value) {
              // If allowEmpty is set, we will not validate falsy fields such as undefined or empty string.
              continue;
            }
            if (taken.has(value)) {
              duplicates.add(value);
            } else {
              taken.add(value);
            }
          }

          for (let index = 0; index < values.length; index++) {
            if (duplicates.has(values[index])) {
              addError(ErrorSources.Table, index, field.key as T, {
                level: validation.level || 'error',
                message: validation.errorMessage || 'Field must be unique'
              });
            }
          }
          break;
        }
        case 'required': {
          const dataToValidate = changedRowIndexes ? changedRowIndexes.map((index) => data[index]) : data;
          for (let i = 0; i < dataToValidate.length; i++) {
            const entry = dataToValidate[i];
            const realIndex = changedRowIndexes ? changedRowIndexes[i] : i;
            if (
              entry?.[field.key as T] === null ||
              entry?.[field.key as T] === undefined ||
              entry[field.key as T] === ''
            ) {
              addError(ErrorSources.Row, realIndex as number, field.key as T, {
                level: validation.level || 'error',
                message: validation.errorMessage || 'Field is required'
              });
            }
          }
          break;
        }
        case 'regex': {
          const dataToValidate = changedRowIndexes ? changedRowIndexes.map((index) => data[index]) : data;
          const regex = new RegExp(validation.value, validation.flags);
          for (let i = 0; i < dataToValidate.length; i++) {
            const entry = dataToValidate[i];
            const realIndex = changedRowIndexes ? changedRowIndexes[i] : i;
            const value = entry?.[field.key as T]?.toString() ?? '';
            if (!value.match(regex)) {
              addError(ErrorSources.Row, realIndex as number, field.key as T, {
                level: validation.level || 'error',
                message:
                  validation.errorMessage || `Field did not match the regex /${validation.value}/${validation.flags} `
              });
            }
          }
          break;
        }
      }
    }
  }

  return data.map((value, index) => {
    // This is required only for table. Mutates to prevent needless rerenders
    if (!('__index' in value)) {
      value.__index = v4();
    }
    const newValue = value as Data<T> & Meta;

    // If we are validating all indexes, or we did full validation on this row - apply all errors
    if (!changedRowIndexes || changedRowIndexes.includes(index)) {
      if (errors[index]) {
        return { ...newValue, __errors: getRowErrors(errors[index], errorsToRemove[index]) };
      }

      if (!errors[index] && value?.__errors) {
        return { ...newValue, __errors: null };
      }
    }
    // if we have not validated this row, keep it's row errors but apply global error changes
    else {
      // at this point errors[index] contains only table source errors, previous row and table errors are in value.__errors
      const hasRowErrors =
        value.__errors && Object.values(value.__errors).some((error) => error.source === ErrorSources.Row);

      if (!hasRowErrors) {
        if (errors[index]) {
          return { ...newValue, __errors: getRowErrors(errors[index], errorsToRemove[index]) };
        }
        return newValue;
      } else if (!!value.__errors || !!errors[index]) {
        // If in row mode - remove field errors from other rows
        return {
          ...newValue,
          __errors: getRowErrors(
            isNotEmptyRowError(errors[index]) ? errors[index] : { ...value.__errors },
            errorsToRemove[index]
          )
        };
      }
      return newValue;
    }

    return newValue;
  });
};

function getRowErrors(rowErrors: RowError, rowErrorsToRemove: Set<string> | undefined): RowError | null {
  const result = Object.entries(rowErrors).reduce((acc, [fieldKey, error]) => {
    if (rowErrorsToRemove?.has(fieldKey)) {
      return acc;
    }
    return {
      ...acc,
      [fieldKey]: error
    };
  }, {});

  return isEmpty(result) ? null : result;
}

function isNotEmptyRowError(error: RowError | undefined | null): error is RowError {
  return !isEmpty(error);
}
